import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IActivo,
  IEventoGenerico,
  IFilter,
  IPopulate,
  IQueryParam,
  IValoresEventoTecnico,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  CrearEditarEventoTecnicoGenericoComponent,
  ICrearEditarEventoTecnicoGenericoData,
} from '../../eventos-genericos/eventos-tecnicos-genericos/crear-editar/crear-editar-evento-tecnico-generico.component';
import { TratamientoGenericoComponent } from '../../eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component';
import { DetallesEventoGenericoComponent } from '../../eventos-genericos/detalles-evento-generico/detalles-evento-generico.component';
import { MatDialog } from '@angular/material/dialog';
import { SegmentadoCambio } from '../../../../componentes/botones';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import { IFiltroTabla } from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { AsignarGenericoComponent } from '../../administracion/eventos-tecnicos-genericos/asignar/asignar.component';
import { LoginService } from '../../../login/login.service';
import { WebSocketService } from '../../../../auxiliares/servicios/websocket.service';
import {
  FILTRO_EVENTOS_TECNICOS,
  ReconciliacionWs,
} from '../../../../auxiliares/servicios/ws-reconciliacion';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import {
  estadoHtml,
  fechaCelda,
  haceCelda,
} from '../../../../auxiliares/tabla/celdas';
import { IEstadoCelda } from '../../../../auxiliares/tabla/tabla.interfaces';
import { EventosGenericosHelperService } from '../../eventos-genericos/helper';

/** Categoría técnica (tipoEvento) con su nombre corto. */
const CATEGORIAS_TECNICAS: { label: string; value: string }[] = [
  { label: 'Alarma', value: 'Evento Técnico Alarma' },
  { label: 'Tracker', value: 'Evento Técnico Tracker' },
  { label: 'Luminaria', value: 'Evento Técnico Luminaria' },
  { label: 'Sirena', value: 'Evento Técnico Sirena' },
  { label: 'Gateway', value: 'Evento Técnico Gateway' },
  { label: 'Vehículo', value: 'Evento Técnico Vehículo' },
];

/** Tipo de solicitud: Servicio técnico o Verificación (pieza estado). */
function tipoSolicitud(filtrador?: string): IEstadoCelda {
  return filtrador === 'Verificación'
    ? { texto: 'Verificación', tono: 'info' }
    : { texto: 'Servicio técnico', tono: 'neutro', icono: 'build' };
}

@Component({
  selector: 'app-listado-servicio-tecnico',
  templateUrl: './listado-servicio-tecnico.component.html',
  styleUrl: './listado-servicio-tecnico.component.scss',
  standalone: false,
})
export class ListadoServicioTecnicoComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(EventosGenericosHttpService);
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private wsService = inject(WebSocketService);

  // Los ST son eventogenerico (filtrador 'Servicio Técnico'/'Verificación'):
  // cada update re-lista con debounce; el polling queda como reconciliación.
  // El filtro va SERVER-SIDE: discriminar por filtrador en el cliente no
  // ahorra nada, el mensaje ya viajó.
  private reconciliacionWs = new ReconciliacionWs(this.wsService, {
    entidades: ['eventogenerico'],
    idPermiso: () => LoginService.getIdPermiso(),
    relistar: () =>
      this.modoAgrupacion === '1' ? this.listar() : this.listarAgrupados(),
    modo: 'trigger',
    filtro: FILTRO_EVENTOS_TECNICOS,
  });

  public name = 'ListadoServicioTecnicoComponent';
  public selectedTab = 0;
  public tipoCliente = HelperService.getTipoCliente();

  // Celdas comunes (huso del navegador, 24 h)
  public fechaCelda = fechaCelda;
  public haceCelda = haceCelda;
  public estadoServicioTecnico =
    EventosGenericosHelperService.estadoServicioTecnico;

  // Pendientes
  public datos: IEventoGenerico[] = [];
  public datos$?: Subscription;
  public totalCount = 0;
  public loading = false;

  // Historial
  public datosHistorico: IEventoGenerico[] = [];
  public datosHistorico$?: Subscription;
  public totalCountHistorico = 0;
  public columnasHistorico?: IColumnas<IEventoGenerico>[];

  // Agrupación
  public modoAgrupacion =
    localStorage.getItem('modoAgrupacionServicioTecnico') || '1';

  // Pendientes agrupados
  public datosAgrupados: IEventoGenerico[] = [];
  public eventosPorCategoria: { [key: string]: IEventoGenerico[] } = {};
  public categorias: string[] = [];
  public eventosPorEntidad: { [key: string]: IEventoGenerico[] } = {};
  public entidades: string[] = [];
  public eventosPorModulo: { [key: string]: IEventoGenerico[] } = {};
  public modulos: string[] = [];
  public eventosPorCliente: { [key: string]: IEventoGenerico[] } = {};
  public clientes: string[] = [];

  // Filtros tabla histórico
  private tipoHistorico: IFiltroTabla = {
    elementos: [
      { label: 'Servicio técnico', value: 'Servicio Técnico' },
      { label: 'Verificación', value: 'Verificación' },
    ],
    filter: {
      field: 'filtrador',
    },
    label: 'Tipo',
    rapido: true,
    tipo: 'select',
    selectLabel: 'label',
    selectValue: 'value',
  };
  private categoriaHistorico: IFiltroTabla = {
    elementos: CATEGORIAS_TECNICAS,
    filter: {
      field: 'tipoEvento',
    },
    label: 'Categoría',
    tipo: 'select',
    selectLabel: 'label',
    selectValue: 'value',
  };
  private fechaHistorico: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Fecha',
    tipo: 'dateRange',
  };
  private clienteHistorico: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  private ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtrosHistorico: IFiltroTabla[] = [
    this.tipoHistorico,
    this.categoriaHistorico,
    this.fechaHistorico,
    this.clienteHistorico,
    this.ancestro,
  ];

  public columnasGrupo =
    this.tipoCliente !== 'Final'
      ? [
          'fecha',
          'tipo',
          'estado',
          'titulo',
          'entidad',
          'tecnico',
          'cliente',
          'acciones',
        ]
      : ['fecha', 'tipo', 'estado', 'titulo', 'entidad', 'tecnico', 'acciones'];

  private populate: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'activo', select: 'identificacion categoria' },
    { path: 'luminaria', select: 'identificacion' },
    { path: 'sirena' },
    {
      path: 'alarma',
      populate: [
        { path: 'domicilio', select: 'direccion identificacion geojson' },
        { path: 'comunicador', select: 'marca modelo' },
        { path: 'modelo', select: 'marca modelo' },
      ],
    },
    { path: 'tracker', select: 'nombre identificacion' },
    { path: 'usuario', select: 'usuario datosPersonales' },
    // detallesTecnicos es un subdocumento, no una referencia: el virtual se
    // popula con la ruta completa (anidado no trae nada y queda "Sin asignar")
    { path: 'detallesTecnicos.tecnico', select: 'usuario datosPersonales' },
  ];

  public filter: IFilter<IEventoGenerico> = {
    // requiereAtencion (flag backend) = atender===true && estado no terminal.
    // ST/Verificación siempre tienen atender:true → equivale a estado!=Finalizado,
    // y usa el índice parcial atencion_activa_v2. Antes: estado:{$ne:'Finalizado'}.
    requiereAtencion: true,
    filtrador: { $in: ['Servicio Técnico', 'Verificación'] },
  } as any;
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    filter: JSON.stringify(this.filter),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public queryHistorico: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name + 'Historico'),
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    const idUsuario = LoginService.getUsuario()?._id;
    (this.filter as any)['$or'] = [
      // Sin técnico asignado → mostrar siempre
      { 'detallesTecnicos.idTecnicoAsignado': { $exists: false } },
      { 'detallesTecnicos.idTecnicoAsignado': null },
      // Con técnico asignado pero no soy yo → mostrar siempre
      {
        'detallesTecnicos.idTecnicoAsignado': { $exists: true, $ne: idUsuario },
      },
      // Soy yo el técnico asignado → aplicar filtro de fecha
      {
        'detallesTecnicos.idTecnicoAsignado': idUsuario,
        $or: [
          { 'detallesTecnicos.fechaDisponibleParaTratar': { $exists: false } },
          { 'detallesTecnicos.fechaDisponibleParaTratar': null },
          {
            'detallesTecnicos.fechaDisponibleParaTratar': {
              $lte: new Date().toISOString(),
            },
          },
        ],
      },
    ];
    this.query.filter = JSON.stringify(this.filter);
    this.loading = true;
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .eventosGenericos(this.query, this.reconciliacionWs.intervaloActual())
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.datos = data.datos;
        this.loading = false;
      });
  }

  public async listarHistorico(): Promise<void> {
    // Histórico = técnicos/verificación finalizados. filtrador + estado fijos
    // usan el índice {filtrador, estado, fechaCreacion} (sort por índice).
    // El filtro fijo se mergea acá para preservar los filtros del usuario.
    const filterObj: Record<string, unknown> = HelperService.stringToObject(
      this.queryHistorico.filter,
    );
    filterObj['estado'] = 'Finalizado';
    if (!filterObj['filtrador']) {
      filterObj['filtrador'] = { $in: ['Servicio Técnico', 'Verificación'] };
    }
    const queryToSend: IQueryParam = {
      ...this.queryHistorico,
      filter: JSON.stringify(filterObj),
    };

    this.datosHistorico$?.unsubscribe();
    this.datosHistorico$ = this.lista2
      .eventosGenericos(queryToSend, this.reconciliacionWs.intervaloActual())
      .subscribe((data) => {
        this.totalCountHistorico = data.totalCount;
        this.datosHistorico = data.datos;
      });
  }

  public async listarAgrupados(): Promise<void> {
    const idUsuario = LoginService.getUsuario()?._id;
    (this.filter as any)['$or'] = [
      // Sin técnico asignado → mostrar siempre
      { 'detallesTecnicos.idTecnicoAsignado': { $exists: false } },
      { 'detallesTecnicos.idTecnicoAsignado': null },
      // Con técnico asignado pero no soy yo → mostrar siempre
      {
        'detallesTecnicos.idTecnicoAsignado': { $exists: true, $ne: idUsuario },
      },
      // Soy yo el técnico asignado → aplicar filtro de fecha
      {
        'detallesTecnicos.idTecnicoAsignado': idUsuario,
        $or: [
          { 'detallesTecnicos.fechaDisponibleParaTratar': { $exists: false } },
          { 'detallesTecnicos.fechaDisponibleParaTratar': null },
          {
            'detallesTecnicos.fechaDisponibleParaTratar': {
              $lte: new Date().toISOString(),
            },
          },
        ],
      },
    ];
    const queryAll: IQueryParam = {
      populate: JSON.stringify(this.populate),
      filter: JSON.stringify(this.filter),
      sort: '-fechaCreacion',
      limit: 0,
      includeChildren: true,
      childrenLevel: 0,
    };
    this.loading = true;
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .eventosGenericos(queryAll, this.reconciliacionWs.intervaloActual())
      .subscribe((data) => {
        this.datosAgrupados = data.datos;
        this.agrupar(data.datos);
        this.loading = false;
      });
  }

  private agrupar(data: IEventoGenerico[]) {
    const obtenerEntidad = (e: IEventoGenerico) =>
      e.alarma?.nombre ||
      e.luminaria?.identificacion ||
      e.tracker?.nombre ||
      e.tracker?.identificacion ||
      e.activo?.identificacion ||
      e.sirena?._id ||
      'Sin asignar';

    const obtenerModulo = (e: IEventoGenerico) => {
      if (e.alarma) return 'Alarma';
      if (e.luminaria) return 'Luminaria';
      if (e.sirena) return 'Sirena';
      if (e.tracker) return 'Tracker';
      if (e.activo) {
        const cat = (e.activo as IActivo).categoria;
        if (cat === 'Vehículo' || cat === 'Colectivo') return cat;
        return cat || 'Vehículo';
      }
      return 'Sin módulo';
    };

    const groupBy = (
      arr: IEventoGenerico[],
      keyFn: (e: IEventoGenerico) => string,
    ) => {
      const keys = [...new Set(arr.map(keyFn))].sort();
      const groups: { [key: string]: IEventoGenerico[] } = {};
      keys.forEach((k) => {
        groups[k] = arr.filter((e) => keyFn(e) === k);
      });
      return { keys, groups };
    };

    const byCat = groupBy(data, (e) => e.tipoEvento || 'Sin categoría');
    const byEnt = groupBy(data, obtenerEntidad);
    const byMod = groupBy(data, obtenerModulo);
    const byCliente = groupBy(data, (e) => e.cliente?.nombre || 'Sin cliente');

    this.eventosPorCategoria = byCat.groups;
    this.categorias = byCat.keys;
    this.eventosPorEntidad = byEnt.groups;
    this.entidades = byEnt.keys;
    this.eventosPorModulo = byMod.groups;
    this.modulos = byMod.keys;
    this.eventosPorCliente = byCliente.groups;
    this.clientes = byCliente.keys;
  }

  public cambioModoAgrupacion(modo: SegmentadoCambio) {
    localStorage.setItem('modoAgrupacionServicioTecnico', modo.value);
    if (modo.value === '1') {
      this.listar();
    } else {
      this.listarAgrupados();
    }
  }

  public obtenerNombreTecnico(dato: IEventoGenerico): string {
    const tecnico = dato.detallesTecnicos?.tecnico;
    if (!tecnico) return 'Sin asignar';
    return tecnico.datosPersonales?.nombre || tecnico.usuario || 'Sin asignar';
  }

  /**
   * Categoría técnica con su nombre corto («Alarma», «Luminaria»…). Antes sólo
   * se veía como el color del borde de la fila.
   */
  public categoriaTecnica(tipoEvento?: string): string {
    if (!tipoEvento) return '';
    return (
      CATEGORIAS_TECNICAS.find((c) => c.value === tipoEvento)?.label ??
      tipoEvento
    );
  }

  public tabChanged(index: number): void {
    this.selectedTab = index;
    if (index === 1 && this.datosHistorico.length === 0) {
      this.listarHistorico();
    }
  }

  public async editar(dato: IEventoGenerico) {
    const data: ICrearEditarEventoTecnicoGenericoData = {
      id: dato._id,
      tipoEvento: dato.tipoEvento,
    };
    const res = this.formularios.abrir(
      CrearEditarEventoTecnicoGenericoComponent,
      {
        tamano: 'grande',
        data,
      },
    );
    res?.afterClosed().subscribe((r) => {
      if (r) this.listar();
    });
  }

  public async asignar(evento: IEventoGenerico) {
    const data = {
      id: evento._id,
      idTecnico: evento.detallesTecnicos?.idTecnicoAsignado,
    };
    const res = this.formularios.abrir(AsignarGenericoComponent, {
      tamano: 'mediano',
      data,
    });
    res?.afterClosed().subscribe((r) => {
      if (r) this.listar();
    });
  }

  public verDetalles(evento: IEventoGenerico): void {
    this.dialog.open(DetallesEventoGenericoComponent, {
      data: { evento },
      width: '900px',
      maxWidth: '95vw',
    });
  }

  public async atender(data: IEventoGenerico) {
    const dialogRef = this.dialog.open(TratamientoGenericoComponent, {
      data: { idEvento: data._id },
      hasBackdrop: false,
      minWidth: '1100px',
      maxWidth: '1500px',
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      // Recargar listado después de tratar
      await this.listar();
    }
  }

  public async eliminar(dato: IEventoGenerico) {
    const res = await this.dialogService.confirm(
      'Eliminar servicio técnico',
      `¿Querés eliminar el evento?`,
    );
    if (!res) return;
    try {
      await this.service.delete(dato._id!);
      this.helper.notifSuccess('Evento eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla histórico

  private setColumnasHistorico() {
    const columnas: IColumnas<IEventoGenerico>[] = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          noData: 'Sin fecha',
        },
      },
      // Tipo
      {
        header: {
          label: 'Tipo',
        },
        row: {
          // Tipo + categoría (la categoría hoy sólo se podía filtrar)
          html: (dato) => {
            const categoria = this.categoriaTecnica(dato.tipoEvento);
            return `<div class="tabla-celda-col"><span>${estadoHtml(tipoSolicitud(dato.filtrador))}</span>${
              categoria
                ? `<span class="tabla-secundario">${escaparHtml(categoria)}</span>`
                : ''
            }</div>`;
          },
        },
      },
      // Título
      {
        header: {
          label: 'Título',
        },
        row: {
          parse(dato) {
            return dato.valores?.titulo;
          },
          noData: 'Sin título',
        },
      },
      // Código de incidente
      {
        header: {
          label: 'Código',
        },
        row: {
          parse(dato) {
            const numeroIncidente = (dato.valores as IValoresEventoTecnico)
              ?.numeroIncidente;
            return numeroIncidente?.toUpperCase();
          },
          noData: 'Sin código',
        },
      },
      // Entidad
      {
        header: {
          label: 'Entidad',
        },
        row: {
          html: (dato) => {
            let icono = '';
            let nombre = '';
            if (dato.alarma?.nombre) {
              icono = 'doorbell';
              nombre = dato.alarma.nombre;
            } else if (dato.luminaria?.identificacion) {
              icono = 'lightbulb';
              nombre = dato.luminaria.identificacion;
            } else if (dato.tracker?.nombre || dato.tracker?.identificacion) {
              icono = 'gps_fixed';
              nombre = dato.tracker.nombre || dato.tracker.identificacion;
            } else if (dato.sirena?._id) {
              icono = 'campaign';
              nombre = dato.sirena._id;
            } else if (dato.activo?.identificacion) {
              icono = 'directions_car';
              nombre = dato.activo.identificacion;
            }
            if (!nombre) return '';
            return `<div class="tabla-linea"><span class="material-symbols-outlined">${icono}</span><span>${escaparHtml(nombre)}</span></div>`;
          },
          noData: 'Sin asignar',
        },
      },
      // Técnico
      {
        header: {
          label: 'Técnico',
        },
        row: {
          html: (dato) => {
            const foto =
              dato.detallesTecnicos?.tecnico?.datosPersonales?.foto ||
              'assets/images/anon.png';
            const nombre = escaparHtml(this.obtenerNombreTecnico(dato));
            return `<div class="tabla-celda-fila"><img class="st-foto" src="${escaparHtml(foto)}" alt="Foto" /><span${dato.detallesTecnicos?.tecnico ? '' : ' class="no-info"'}>${nombre}</span></div>`;
          },
        },
      },
    ];

    // Cliente
    if (this.tipoCliente !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.verDetalles(dato),
          },
        ],
      },
    });

    this.columnasHistorico = columnas;
  }

  async ngOnInit() {
    this.setColumnasHistorico();
    this.reconciliacionWs.iniciar();
    if (this.modoAgrupacion === '1') {
      await this.listar();
    } else {
      await this.listarAgrupados();
    }
  }

  ngOnDestroy() {
    this.reconciliacionWs.detener();
    this.datos$?.unsubscribe();
    this.datosHistorico$?.unsubscribe();
  }
}
